// StakePostCard.jsx
import React from 'react';
import './StakePostCard.css';

function safeText(s) {
  return (s || '').toString().replace(/\s+/g, ' ').trim();
}

function shortText(s, n = 90) {
  const t = safeText(s);
  if (!t) return '';
  return t.length > n ? t.slice(0, n - 1) + '…' : t;
}

// ✅ Same idea as Post.js (lightweight)
const classifyMediaUrl = (url = '') => {
  const u = String(url).toLowerCase().split('?')[0];
  if (u.endsWith('.mp4') || u.endsWith('.webm') || u.endsWith('.mov')) return 'video';
  if (
    u.endsWith('.jpg') ||
    u.endsWith('.jpeg') ||
    u.endsWith('.png') ||
    u.endsWith('.gif') ||
    u.endsWith('.webp')
  ) return 'image';
  if (u.includes('/videos/')) return 'video';
  return 'image';
};

// ✅ Rule:
// - show ONLY first image (first image in media list)
// - if media is video-only, show post.thumbnail
function getThumb(post) {
  if (!post) return null;

  const list = Array.isArray(post.media) ? post.media : (post.media ? [post.media] : []);

  const norm = list
    .map((m) => {
      if (!m) return null;
      if (typeof m === 'string') return { url: m, type: classifyMediaUrl(m) };
      if (typeof m === 'object' && typeof m.url === 'string')
        return { url: m.url, type: classifyMediaUrl(m.url) };
      return null;
    })
    .filter(Boolean);

  // 1) First IMAGE only
  const firstImage = norm.find((m) => m.type === 'image' && m.url);
  if (firstImage?.url) return firstImage.url;

  // 2) No images -> if any VIDEO exists, use thumbnail
  const hasVideo = norm.some((m) => m.type === 'video');
  if (hasVideo && typeof post.thumbnail === 'string' && post.thumbnail.length > 5) {
    return post.thumbnail;
  }

  return null;
}

function getRole(post) {
  const u = post?.user || {};
  const now = new Date();

  const paidUntilOk = u.merchantPaidUntil && new Date(u.merchantPaidUntil) > now;
  const explicitlyPaid = u.paymentStatus === 'paid';
  const onTrial = u.paymentStatus === 'trial' && paidUntilOk;
  const active = u.merchantActive === true || explicitlyPaid || onTrial || paidUntilOk;

  return active ? 'MERCHANT' : 'PIONEER';
}

export default function StakePostCard({ post, onClick }) {
  if (!post) return null;

  const u = post.user || {};
  const avatar = post.profilePic || u.profilePic;
  const name = post.name || u.name || 'BarterFi User';

  // If content empty (repost-only, etc), keep it clean:
  const caption = shortText(post.content, 95);

  const thumb = getThumb(post);
  const role = getRole(post);
  const rolePillClass = role === 'MERCHANT' ? 'merchant-pill' : 'pioneer-pill';

  const handleKeyDown = (e) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className="stake-card"
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="stake-card-left">
        <div className="stake-topline">
          <div className="stake-avatar">
            {avatar ? <img src={avatar} alt="" /> : <div className="stake-avatar-ph" />}
          </div>

          <div className="stake-meta">
            <div className="stake-name-row">
              <span className="stake-name" title={name}>
                {name}
              </span>
            </div>

            {/* ✅ Role pill sits where username used to be */}
            <div className={`stake-role-pill ${rolePillClass}`}>{role}</div>
          </div>
        </div>

        {caption ? <div className="stake-caption">{caption}</div> : null}
      </div>

      <div className="stake-card-right">
        {thumb ? (
          <img className="stake-thumb" src={thumb} alt="" loading="lazy" />
        ) : (
          <div className="stake-thumb stake-thumb-ph">
            <span>See post</span>
          </div>
        )}
      </div>
    </div>
  );
}
